import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { getBlog } from "../lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    getBlog().then(setPosts).catch(() => {});
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="blog-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.25em] text-gold">The Journal</span>
          <h1 className="font-serif text-5xl md:text-7xl font-light mt-4 leading-none text-foreground">
            Notes on <span className="text-gradient-gold italic">innovation & impact.</span>
          </h1>
          <p className="mt-6 text-muted-foreground md:text-lg font-light max-w-2xl">
            Essays on medical innovation, research, writing and mentorship — from the ward to the lab to the pitch stage.
          </p>
        </Reveal>

        {featured && (
          <Reveal delay={0.1}>
            <Link to={`/blog/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`}
              className="group mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8 rounded-3xl border border-gold/15 bg-card overflow-hidden hover:border-gold/40 transition-colors duration-500">
              <div className="relative h-64 lg:h-auto overflow-hidden">
                <img src={featured.cover_image} alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/40" />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.15em] text-gold/80 mb-4">
                  <span>{featured.category}</span><span>·</span><span>{featured.read_time}</span>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-foreground group-hover:text-gold transition-colors leading-tight">
                  {featured.title}
                </h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.12em] text-gold">
                  Read essay <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
                </span>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {rest.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}>
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                className="group block rounded-2xl border border-gold/15 bg-card overflow-hidden hover:border-gold/40 transition-colors duration-500 h-full">
                <div className="relative h-52 overflow-hidden">
                  <img src={p.cover_image} alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-[0.15em] text-gold/80 mb-3">
                    <span>{p.category}</span><span>·</span><span>{p.read_time}</span>
                  </div>
                  <h3 className="font-serif text-2xl text-foreground group-hover:text-gold transition-colors leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.excerpt}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
